import React, { useEffect } from 'react';

import { ChatMessage } from '../../services/chatService';
import { BrandPendulumMark } from '../BrandPendulumMark';
import { PlanningStartMessage } from '../PlanningStartProvider';

import {
  AgentSuggestionButtons,
  EmptyPlaceholder,
  MessagesList,
  SingleAgentTyping,
  useOverviewHasItems,
} from './ChatBoxMessages';

type MessagesContainerProps = {
  projectId?: string | null;
  sessionId?: string | null;
  messages: ChatMessage[];
  userDisplayName: string;
  isAiTyping: boolean;
  streamingDisplay: string;
  currentStreamingMsgId?: string | null;
  chatContainerRef: React.RefObject<HTMLDivElement | null>;
  messagesEndRef: React.RefObject<HTMLDivElement | null>;
  onScroll: () => void;
  isProjectLoading?: boolean;
  isSessionResolving?: boolean;
  sessionLoadFailed?: boolean;
};

/** Broadcast agent typing state to neural net canvas. */
const useBroadcastTyping = (isAiTyping: boolean, agentsAtWork: boolean) => {
  useEffect(() => {
    if (!isAiTyping && !agentsAtWork) return undefined;
    const id = setInterval(() => window.dispatchEvent(new Event('chat_typing')), 500);

    return () => clearInterval(id);
  }, [isAiTyping, agentsAtWork]);
};

const ProjectBootstrapLoader: React.FC = () => (
  <div
    className="planning-session-loader planning-session-loader--bootstrap"
    role="status"
    aria-live="polite"
  >
    <BrandPendulumMark animated={false} className="planning-session-loader__mark" />
    <span>Loading messages...</span>
  </div>
);

const SessionLoadError: React.FC = () => (
  <div className="planning-session-loader planning-session-loader--error" role="status" aria-live="polite">
    <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
    <span>Could not load session history.</span>
  </div>
);

const SessionResolvingLoader: React.FC = () => (
  <div className="planning-session-loader" role="status" aria-live="polite">
    <BrandPendulumMark animated={false} className="planning-session-loader__mark" />
    <span>Loading session...</span>
  </div>
);

type MessagesBodyProps = Pick<MessagesContainerProps,
  'projectId' | 'sessionId' | 'messages' | 'userDisplayName' | 'isAiTyping'
  | 'streamingDisplay' | 'currentStreamingMsgId' | 'isProjectLoading'>;

const MessagesBody: React.FC<MessagesBodyProps> = (props) => {
  const overviewHasItems = useOverviewHasItems();
  const hasAssistantMessages = props.messages.some((message) => (
    message.author === 'assistant'
    && (!props.sessionId || message.session_id === props.sessionId)
  ));
  const renderedMessages = props.isAiTyping && props.currentStreamingMsgId
    ? props.messages.filter((message) => message.id !== props.currentStreamingMsgId)
    : props.messages;

  return (
    <>
      <PlanningStartMessage surface="chat" />
      {props.isAiTyping && <SingleAgentTyping content={props.streamingDisplay} />}

      {/* Fallback pills render when overview is empty; otherwise they live in UnifiedActionBar. */}
      {!overviewHasItems && !hasAssistantMessages && (
        <AgentSuggestionButtons projectId={props.projectId} sessionId={props.sessionId} />
      )}

      {renderedMessages.length === 0 ? (
        props.isProjectLoading ? <ProjectBootstrapLoader /> : <EmptyPlaceholder />
      ) : (
        <MessagesList
          messages={renderedMessages}
          userDisplayName={props.userDisplayName}
          projectId={props.projectId}
          sessionId={props.sessionId}
        />
      )}
    </>
  );
};

export function MessagesContainer({
  projectId, sessionId, messages, userDisplayName, isAiTyping, streamingDisplay,
  currentStreamingMsgId, chatContainerRef, messagesEndRef, onScroll, isProjectLoading,
  isSessionResolving = false, sessionLoadFailed = false,
}: MessagesContainerProps): React.ReactElement {
  useBroadcastTyping(isAiTyping, false);

  return (
    <div
      ref={chatContainerRef as React.RefObject<HTMLDivElement>}
      className="planning-messages"
      onScroll={onScroll}
    >
      <div ref={messagesEndRef as React.RefObject<HTMLDivElement>} />

      {sessionLoadFailed ? (
        <SessionLoadError />
      ) : isSessionResolving ? (
        <SessionResolvingLoader />
      ) : (
        <MessagesBody projectId={projectId} sessionId={sessionId} messages={messages}
          userDisplayName={userDisplayName} isAiTyping={isAiTyping}
          streamingDisplay={streamingDisplay} currentStreamingMsgId={currentStreamingMsgId}
          isProjectLoading={isProjectLoading} />
      )}
    </div>
  );
}
